import * as React from 'react'
import {
  OpenInNewWindowIcon,
  CheckCircledIcon,
  CheckIcon,
} from '@radix-ui/react-icons'
import { z } from 'zod'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import {
  DialogClose,
  DialogDescription,
  DialogDivider,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import { LabeledInput } from '~/sotano-kit/primitives/input'
import { LabeledCheckbox } from '~/sotano-kit/primitives/checkbox'
import LoadingIcon from '../../../(repo)/[repo-slug]/(editor)/explore/components/blocks/empty-state/loading-icon'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import { useMutation } from '@tanstack/react-query'
import { CreateTeamInputSchema } from '~/app/api/teams/schema'
import { clsx } from 'clsx'
import Link from 'next/link'
import { usePlansData } from '~/lib/stripe/prices/client'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { BillingSwitcher } from '~/components/billing'
import { CheckoutSessionInput } from '~/app/api/stripe/checkout-sessions/input'
import { useSearchParams } from 'next/navigation'
import { radioClasses } from '~/sotano-kit/primitives/radio-group'

export const TeamForm = ({ handleClose }: { handleClose: () => void }) => {
  const { team } = useSelectedTeam()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const searchParams = useSearchParams()
  const plans = usePlansData()
  const [selectedPlan, setSelectedPlan] = React.useState<'team' | 'unlimited'>(
    searchParams.get('selected-plan') === 'unlimited' ? 'unlimited' : 'team'
  )
  const [selectedBilling, setSelectedBilling] = React.useState<
    'monthly' | 'yearly'
  >('monthly')

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('selected-plan') === 'unlimited') {
      setSelectedPlan('unlimited')
    } else if (searchParams.get('selected-plan') === 'team') {
      setSelectedPlan('team')
    }
    if (searchParams.get('billing') === 'yearly') {
      setSelectedBilling('yearly')
    } else if (searchParams.get('billing') === 'monthly') {
      setSelectedBilling('monthly')
    }
  }, [])

  const [checkoutState, setCheckoutState] = React.useState<
    'idle' | 'loading' | 'redirecting'
  >('idle')

  const { mutate } = useMutation(
    async ({
      title,
      billing,
      plan,
    }: {
      title: CreateTeamInputSchema['title']
      billing: 'monthly' | 'yearly'
      plan: 'team_v2' | 'unlimited'
    }) => {
      if (!team) return
      if (!title) return
      setCheckoutState('loading')
      const searchParams = new URLSearchParams(window.location.search)
      const discountCode = searchParams.get('discount-code')
      const freeTrial = searchParams.get('free-trial')

      const res = await fetch('/api/stripe/checkout-sessions', {
        method: 'POST',
        body: JSON.stringify({
          title,
          plan,
          slug: team.slug,
          strategy: 'create-team',
          billing,
          freeTrial: freeTrial === 'true' || freeTrial === '1',
          ...(discountCode && { discountCode }),
        } satisfies CheckoutSessionInput),
      })
      const json = await res.json()
      const { checkoutUrl } = z
        .object({ checkoutUrl: z.string().url() })
        .parse(json)
      setCheckoutState('redirecting')

      window.location.href = checkoutUrl
    },
    {
      onError: () => {
        setCheckoutState('idle')
      },
    }
  )

  const data = plans.data

  return (
    <form
      method="POST"
      onChange={async (e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title') as string
        const tosAccepted = (formData.get('tos') as string) === 'on'
        if (tosAccepted && !title && inputRef?.current) inputRef.current.focus()
      }}
      onSubmit={async (e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title') as string
        const tosAccepted = (formData.get('tos') as string) === 'on'
        const plan = selectedPlan === 'team' ? 'team_v2' : 'unlimited'
        if (!title || !tosAccepted) return

        mutate({ title, billing: selectedBilling, plan })
      }}
    >
      <DialogHeader>
        <DialogTitle>Create new team</DialogTitle>
        <DialogDescription>
          Select your BaseHub Plan and Bring your people together.
        </DialogDescription>
      </DialogHeader>
      <DialogDivider />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <LabeledInput
            blocked={false}
            inputProps={{ autoComplete: 'off', required: true }}
            className="!pb-1 !pt-0"
            label="Team Name"
            name="title"
            ref={inputRef}
          />
          <LabeledCheckbox
            className=""
            checkboxProps={{ required: true }}
            label={
              <>
                I accept the&nbsp;
                <ButtonLink
                  onMouseOver={(e) => e.stopPropagation()}
                  unstyled
                  focusStyled
                  href="/terms-and-conditions"
                  target="_blank"
                  rel="noopener noreferer"
                  className="hover:text-dark-control-base space-x-0.5 whitespace-nowrap"
                >
                  <span className="underline">Terms of Service</span>
                  <OpenInNewWindowIcon
                    width={14}
                    height={14}
                    className="inline-block mb-0.5"
                  />
                </ButtonLink>
              </>
            }
            name="tos"
            id="tos"
            horizontal="reverse"
          />
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <BillingSwitcher
              selectedBilling={selectedBilling}
              setSelectedBilling={setSelectedBilling}
              monthly="Monthly"
              yearly={
                <span>
                  <span className="mr-1">Yearly</span>
                  <span className="font-semibold text-dark-control-base text-[11px] tracking-tight">
                    Save 20%
                  </span>
                </span>
              }
            />
          </div>
          <div>
            <div className="flex gap-4 py-3">
              {plans.isLoading || !data ? (
                <>
                  <SkeletonTeamRadioInput />
                  <SkeletonTeamRadioInput />
                </>
              ) : (
                <>
                  <TeamRadioInput
                    title={data.plans.team_v2.title}
                    price={
                      data.plans.team_v2.pricing[selectedBilling]?.perMonth ??
                      null
                    }
                    perUser={data.plans.team_v2.perUser}
                    features={data?.plans.team_v2.marketingData.features ?? []}
                    billed={selectedBilling}
                    selected={selectedPlan === 'team'}
                    name="plan"
                    onChange={() => setSelectedPlan('team')}
                  />
                  <TeamRadioInput
                    title={data?.plans.unlimited.title ?? null}
                    price={
                      data?.plans.unlimited.pricing[selectedBilling]
                        ?.perMonth ?? null
                    }
                    perUser={data.plans.unlimited.perUser}
                    features={
                      data?.plans.unlimited.marketingData.features ?? []
                    }
                    billed={selectedBilling}
                    selected={selectedPlan === 'unlimited'}
                    name="plan"
                    onChange={() => setSelectedPlan('unlimited')}
                  />
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Link
                href="/pricing"
                target="_blank"
                className="text-sm text-dark-muted underline hover:text-dark-dim"
              >
                Read more about our pricing
              </Link>
              <div className="border-r border-dark-border-solid h-4" />
              <Link
                href="mailto:support+enterprise@basehub.com"
                className="text-sm text-dark-muted underline hover:text-dark-dim"
              >
                Contact us for custom pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
      <DialogFooter divider>
        <p className="text-xs text-dark-faint self-center items-center mr-auto flex">
          Payment secured by Stripe.
        </p>
        <div className="flex gap-2">
          <Button type="button" intent="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            icon={
              checkoutState !== 'idle' && (
                <LoadingIcon status="loading" className="!text-button-label" />
              )
            }
          >
            {checkoutState === 'redirecting'
              ? 'Redirecting...'
              : checkoutState === 'loading'
              ? 'Loading...'
              : 'Continue to Payment'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  )
}
export const TeamRadioInput = ({
  title,
  price,
  features,
  billed,
  selected,
  name,
  perUser,
  onChange,
}: {
  title: string | null
  price: number | null
  features: string[]
  billed: 'monthly' | 'yearly'
  selected: boolean
  name: string
  perUser?: boolean
  onChange: () => void
}) => {
  return (
    <label
      className={clsx(
        'flex flex-col relative p-4 flex-1 pt-3 gap-4 rounded-lg bg-dark-base-dark border border-dark-border-solid ',
        selected && 'border-dark-control-base'
      )}
      onClick={onChange}
    >
      <div className="flex flex-col">
        <h5 className="text-sm font-medium text-dark-dim">{title}</h5>
        <p className="text-xl text-dark-bright">
          ${price}{' '}
          <span className="text-dark-muted text-xs">
            {perUser && 'user'}/month
            {billed === 'yearly' && ', billed annually'}
          </span>
        </p>
      </div>
      <ul className="flex flex-col gap-1.5 text-dark-dim items-start">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-1.5 text-xs">
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <div className="absolute top-4 z-10 right-4">
        <label htmlFor={name} className={radioClasses.indicator} />

        <span
          className={clsx('-mr-1 shrink-0', radioClasses.item)}
          data-state={selected ? 'checked' : 'unchecked'}
        >
          {selected && <span className={radioClasses.indicator} />}
        </span>
      </div>
    </label>
  )
}

const SkeletonTeamRadioInput = () => {
  return (
    <label
      className={clsx(
        'flex flex-col relative p-4 flex-1 pt-3 gap-4 rounded-lg bg-dark-border border border-dark-border-solid'
      )}
    >
      <div className="flex flex-col gap-2">
        <Skeleton width={40} height={18} />
        <Skeleton width={133} height={31} />
      </div>
      <ul className="flex flex-col gap-1.5 text-dark-dim items-start">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-center gap-1.5 text-xs">
            <Skeleton width={160} height={19.5} />
          </li>
        ))}
      </ul>
    </label>
  )
}

export const TeamFormV1: React.FC<{ handleClose: () => void }> = ({
  handleClose,
}) => {
  const { team } = useSelectedTeam()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [checkoutState, setCheckoutState] = React.useState<
    'idle' | 'loading' | 'redirecting'
  >('idle')

  const { mutate, error } = useMutation(
    async ({
      title,
      billing,
    }: {
      title: CreateTeamInputSchema['title']
      billing: 'monthly' | 'yearly'
    }) => {
      setCheckoutState('loading')
      const searchParams = new URLSearchParams(window.location.search)
      const discountCode = searchParams.get('discount-code')
      const res = await fetch('/api/stripe/checkout-sessions', {
        method: 'POST',
        body: JSON.stringify({
          title,
          plan: 'team_v2',
          slug: team?.slug,
          strategy: 'create-team',
          billing,
          ...(discountCode && { discountCode }),
        }),
      })
      const json = await res.json()
      const { checkoutUrl } = z
        .object({ checkoutUrl: z.string().url() })
        .parse(json)
      setCheckoutState('redirecting')

      window.location.href = checkoutUrl
    },
    {
      onError: () => {
        setCheckoutState('idle')
      },
    }
  )

  return (
    <form
      method="POST"
      onChange={async (e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title') as string
        const tosAccepted = (formData.get('tos') as string) === 'on'
        if (tosAccepted && !title && inputRef?.current) inputRef.current.focus()
      }}
      onSubmit={async (e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title') as string
        const tosAccepted = (formData.get('tos') as string) === 'on'
        const billing = formData.get('billing') === 'on' ? 'yearly' : 'monthly'
        if (!title || !tosAccepted) return

        mutate({ title, billing })
      }}
    >
      <DialogHeader className="p-4">
        <DialogTitle>Create new team</DialogTitle>
        <DialogDescription>Bring your people together.</DialogDescription>
      </DialogHeader>

      <div className="px-4 pt-2 pb-3">
        <LabeledInput
          className="!pt-0"
          label="Team Name"
          name="title"
          blocked={false}
          inputProps={{ autoComplete: 'off', required: true }}
          ref={inputRef}
        />

        <div className="?.firstChildg-dark-shade-hover mt-2 text-sm leading-normal border border-dark-border-solid rounded-md px-3 py-2">
          <p className="flex items-center space-x-2">
            <CheckCircledIcon />
            <span>Teams start at $29/month</span>
          </p>
          <p className="mt-1.5 text-dark-muted">
            Includes unlimited members. Bills monthly depending on Team usage.
            See the full breakdown in our{' '}
            <ButtonLink
              unstyled
              focusStyled
              href="/pricing"
              target="_blank"
              rel="noopener noreferer"
              className="hover:text-dark-control-base underline"
            >
              pricing page
            </ButtonLink>
            .
          </p>
        </div>
        <LabeledCheckbox
          className="py-1 mt-6"
          checkboxProps={{ required: true }}
          label={
            <>
              I accept the&nbsp;
              <ButtonLink
                onMouseOver={(e) => e.stopPropagation()}
                unstyled
                focusStyled
                href="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferer"
                className="hover:text-dark-control-base space-x-0.5 whitespace-nowrap"
              >
                <span className="underline">Terms of Service</span>
                <OpenInNewWindowIcon
                  width={14}
                  height={14}
                  className="inline-block mb-0.5"
                />
              </ButtonLink>
            </>
          }
          name="tos"
          id="tos"
          horizontal="reverse"
        />
      </div>

      <DialogFooter divider>
        {!!error && (
          <span className="text-xs text-red">
            Something went wrong while creating your team.
          </span>
        )}

        <div className="flex items-center sm:space-x-4 ml-auto">
          <DialogClose
            asChild
            onClick={(e) => {
              e.preventDefault()
              handleClose()
            }}
          >
            <Button
              size="large"
              type="button"
              intent="secondary"
              disabled={checkoutState !== 'idle'}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="large"
            disabled={checkoutState !== 'idle'}
            type="submit"
            icon={
              checkoutState !== 'idle' && (
                <LoadingIcon status="loading" className="!text-inherit" />
              )
            }
          >
            {checkoutState === 'redirecting'
              ? 'Redirecting...'
              : checkoutState === 'loading'
              ? 'Loading...'
              : 'Start Set Up'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  )
}
